import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//components
import DataView from 'Components/DataView';
import { Button, Select, Tooltip } from "@gpstrackit/react-controls";
import ChipStatus from 'Components/ChipStatus';
//Helpers
import { idsValidation, getPMUser, getDateFormatted } from 'Core/data/Helpers';

//Views
import CreateEditCannedCommand from 'Modules/manufacturers/.components/cannedCommandsTab/.components/createEditCannedCommand';
import DeleteCannedCommand from 'Modules/manufacturers/.components/cannedCommandsTab/.components/deleteCannedCommand';

//Actions
import {
    cannedCommandCreateEditOpenModal,
    cannedCommandDeleteOpenModal,
    searchEntityRequest,
    clearRedux,
} from 'Redux/actions';

//Styles
import './index.scss';

//Permission
import { HasPermission } from "Core/security"

const CannedCommandsTab = (props) => {
    const dispatch = useDispatch();
    const { height, manufacturer } = props;
    const { messages } = useIntl();
    const tableRef = useRef();

    const openModalCreateCannedCommand = useSelector(state => state.createEditCannedCommandRedux.openModal);
    const openModalDeleteCannedCommand = useSelector(state => state.deleteCannedCommandRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const unitTypes = useSelector(state => state.generalRedux.unitTypes);
    const user = useSelector(state => state.securityRedux.user);

    const [unitType, setUnitType] = useState(null);

    const [columns, setColumns] = useState([
        {
            key: "label",
            title: messages['canned_commands_tab_label'],
            field: "label",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.label',
            code: "canned_commands_tab_label",
            render: (item) => {
                return <span>{item?.label}</span>
            }
        },
        {
            key: "command",
            field: "command",
            title: messages['canned_commands_tab_command'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.command',
            code: "canned_commands_tab_command",
            render: (item) => {
                return <span>
                    {
                        item?.command.length > 50 &&
                        <Tooltip title={item?.command}>
                            <div>
                                {`${item?.command.substring(0, 50)}...`}
                            </div>
                        </Tooltip>
                    }
                    {
                        item?.command.length <= 50 &&
                        item?.command
                    }
                </span>
            }
        },
        {
            key: "via",
            title: messages['canned_commands_tab_via'],
            field: "via",
            show: true,
            resize: true,
            width: 100,
            export: true,
            sort: true,
            functionString: 'return row?.via',
            code: "canned_commands_tab_via",
            render: (item) => {
                return <span>{item?.via}</span>
            }
        },
        {
            key: "status",
            title: messages['canned_commands_tab_status'],
            field: "status",
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.status',
            code: "canned_commands_tab_status",
            render: (item) => {
                return <ChipStatus messages={messages} statusCode={item.status === "Active" ? 1 : 20} />
            }
        },
        {
            key: "unitType_name",
            title: messages['canned_commands_tab_unit_type'],
            field: "unitType.name",
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.unitType?.name',
            code: "canned_commands_tab_unit_type",
            render: (item) => {
                return <span>{item?.unitType?.name}</span>
            }
        },
        {
            key: "appliesToAllUnitTypes",
            title: messages['canned_commands_tab_apply_to_all'],
            field: "appliesToAllUnitTypes",
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.appliesToAllUnitTypes',
            code: "canned_commands_tab_apply_to_all",
            render: (item) => {
                return <span>{item?.appliesToAllUnitTypes ? "Y" : "N"}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['canned_commands_tab_created_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "canned_commands_tab_created_on",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['canned_commands_tab_created_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: 'return row?.createdBy',
            code: "canned_commands_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['canned_commands_tab_updated_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "canned_commands_tab_updated_on",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['canned_commands_tab_updated_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: 'return row?.updatedBy',
            code: "canned_commands_tab_updated_by",
            render: (item) => {
                return <span>{item?.updatedBy && getPMUser(item?.updatedBy, pmUsers)}</span>
            }
        }
    ])
    const [searchFields, setSearchFields] = useState(["label", "via", "command", "status"]);

    const unitTypesHandler = (unitType) => {

        //save preferences
        let currentNewConditions = tableRef?.current?.conditions;

        currentNewConditions = currentNewConditions?.filter((item) => item?.fields[0] !== 'unitTypeId');


        if (unitType)
            currentNewConditions.push({ fields: ["unitTypeId"], terms: [unitType.id], conditionOperator: "AND" })

        tableRef?.current?.getData(null, null, null, currentNewConditions);
        setUnitType(unitType);
    }

    useEffect(() => {
        pmUsers && pmUsers.length === 0 && dispatch(searchEntityRequest({ key: "pmUsers" }));
        unitTypes && unitTypes.length === 0 && dispatch(searchEntityRequest({ key: "unitTypes" }));
    }, []);

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                columns={columns}
                dinamicQuery
                name={`pm-cannedcommands-tab-${manufacturer?.name}`}
                exportName="cannedcommands"
                entity="canned-commands-tab"
                graphqlFunction="searchCannedCommands"
                graphqlSort="CannedCommandSortInput"
                graphqlConditions="CannedCommandSearchInput"
                customQuery="id"
                hideAllInPagination
                //search
                asyncSelect
                searchConditions={["label", "via", "command", "status"]}
                searchOptionValue="id"
                searchOptionLabel="label"
                customSearch={{ fields: ["manufacturerId"], terms: [manufacturer?.id], exact: true }}
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    let currentConditions = {}
                    let currentTags = {}
                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }

                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'label' && item?.fields[0] !== 'manufacturerId' && item?.fields[0] !== 'unitTypeId');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }
                    let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.label))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions && currentConditions.push({ fields: ["manufacturerId"], terms: [manufacturer?.id], exact: true, conditionOperator: "AND" });
                    unitType && currentConditions && currentConditions.push({ fields: ["unitTypeId"], terms: [unitType.id], conditionOperator: "AND" })

                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions);
                }}
                searchFields={searchFields}
                noSplit
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                noFilters
                height={height}
                options={[
                    {
                        id: "edit-canned-command",
                        label: messages['edit'],
                        show: true,
                        disabled: !HasPermission(user, "cancom", "u"),
                        single: true,
                        action: (items) => {
                            dispatch(cannedCommandCreateEditOpenModal(items[0]));
                        }
                    },
                    {
                        id: "delete-canned-command",
                        label: messages['delete'],
                        show: true,
                        disabled: !HasPermission(user, "cancom", "d"),
                        single: true,
                        action: (items) => {
                            dispatch(cannedCommandDeleteOpenModal(items[0]));
                        }
                    },
                ]}
                extraButton={() => {
                    return (
                        <div style={{ width: 160, paddingLeft: 2 }}>
                            <Button
                                onClick={() => dispatch(cannedCommandCreateEditOpenModal())}
                                className="btn-dark-blue"
                                disabled={!HasPermission(user, "cancom", "c")}
                                style={{ height: 42, width: 160 }}
                            >
                                {messages['canned_commands_tab_add_new']}
                            </Button>
                        </div>
                    )
                }}
                menuOptions={() => {
                    return [
                        messages['edit'],
                        messages['delete']
                    ]
                }}
                onClickMenu={(event, item) => {
                    if (event === messages['edit']) {
                        if (HasPermission(user, "cancom", "u")) {
                            dispatch(cannedCommandCreateEditOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['edit'],
                                3000,
                            );
                        }
                    }
                    if (event === messages['delete']) {
                        if (HasPermission(user, "cancom", "d")) {
                            dispatch(cannedCommandDeleteOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['delete'],
                                3000,
                            );
                        }
                    }
                }}
                extraFilters={() => {
                    return (
                        <div className="extra-filters-tab" style={{ width: '20%', justifyContent: 'end' }}>
                            <Select
                                id="unitType"
                                placeholder={messages['canned_commands_tab_unit_type']}
                                options={unitTypes ? unitTypes.filter((item) => item?.manufacturerId === manufacturer?.id) : []}
                                value={unitType}
                                textField="name"
                                valueField="id"
                                onChange={(items = []) => unitTypesHandler(items)}
                                isClearable
                            />
                        </div>
                    )
                }}
            />
            {openModalCreateCannedCommand && <CreateEditCannedCommand manufacturer={manufacturer} tableRef={tableRef} />}
            {openModalDeleteCannedCommand && <DeleteCannedCommand tableRef={tableRef} />}
        </div>
    )
}

export default CannedCommandsTab